/*
 * A visit's everyday edit boxes, shared by the visit page and the visit card that pulls out of the schedule (VisitDrawer):
 * Edit schedule, Team, Line items (with credit or don't credit when its invoice is paid), Did the client pay you on
 * Complete, and on a repeating job the "apply to future visits" box after a save. A visit that was only computed becomes a
 * row the first time it changes, so `go` gets the ref it has after each save.
 */
import { useState } from "react";
import type { VisitDetail } from "@steward/shared";
import {
  useCompleteVisit,
  usePeople,
  useReopenVisit,
  useSettings,
  useVisitCrew,
  useVisitFuture,
  useVisitLines,
  type LineInput,
} from "../api/hooks";
import { ApiError } from "../api/client";
import { useSession } from "../app/session";
import { Choice } from "./fields";
import { Modal, useToast } from "./overlays";
import { Banner, Button } from "./parts";
import { FutureSheet, type FutureKey } from "./repeat";
import { draftLine, errText, LinesEditor, linesOut, ScheduleSheet, TeamSheet, type DraftLine } from "./work";

export function useVisitSheets(v: VisitDetail | undefined, go: (ref: string) => void) {
  const { company, can } = useSession();
  const toast = useToast();
  const settings = useSettings(company.id);
  const people = usePeople(company.id, can("visit.update"));
  const complete = useCompleteVisit();
  const reopen = useReopenVisit();
  const crew = useVisitCrew();
  const lines = useVisitLines();
  const applyFuture = useVisitFuture();
  const [sheet, setSheet] = useState<"schedule" | "team" | "lines" | "paid" | null>(null);
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [lineError, setLineError] = useState("");
  /** The visit's paid invoice would go down: the owner picks credit or don't credit, as on Edit paid invoice. */
  const [needCredit, setNeedCredit] = useState(false);
  /** A repeating job's visit was just saved: ask what the visits after it take (Jobber's Apply to future visits). */
  const [future, setFuture] = useState<{ ref: string; date: string; ticked: FutureKey[] } | null>(null);
  const [paid, setPaid] = useState<"cash" | "company" | "no">("no");
  const fail = (e: unknown) => toast(errText(e), { kind: "error" });
  const close = () => setSheet(null);

  /** After a save on a repeating job's visit that is still to come: open the future box. True when it opened. */
  const askFuture = (d: { ref: string; date: string; status: string }, ticked: FutureKey[]) => {
    if (!v?.repeating || d.status !== "scheduled") return false;
    setFuture({ ref: d.ref, date: d.date, ticked });
    return true;
  };
  const open = (s: "schedule" | "team" | "lines") => {
    if (!v) return;
    if (s === "lines") {
      setDraft(v.lines.length ? v.lines.map((l) => draftLine(l)) : [draftLine()]);
      setLineError("");
      setNeedCredit(false);
    }
    setSheet(s);
  };
  const doComplete = (crewCollected?: "cash" | "company" | "no") => {
    if (!v) return;
    complete.mutate(
      { ref: v.ref, ...(crewCollected ? { crewCollected } : {}) },
      {
        onSuccess: (d) => {
          setSheet(null);
          go(d.ref);
          toast(`${v.clientName} marked done`, {
            undo: () =>
              reopen.mutate(d.ref, {
                onSuccess: (x) => {
                  go(x.ref);
                  toast("Visit reopened");
                },
                onError: fail,
              }),
          });
        },
        onError: fail,
      },
    );
  };
  /** Complete, asking first whether the client paid the crew when the brand's crew take money. */
  const markDone = () => ((settings.data?.settings.crew.collectsMoney ?? false) ? setSheet("paid") : doComplete());
  const saveLines = (credit?: "credit" | "no_credit") => {
    if (!v) return;
    const out = linesOut(draft);
    if (typeof out === "string") {
      setLineError(out);
      return;
    }
    const before = v.lines.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unitPriceCents: l.unitPriceCents,
      frequency: l.frequency as LineInput["frequency"],
      ...(l.note ? { note: l.note } : {}),
    }));
    lines.mutate(
      { ref: v.ref, lines: out, scope: "this", ...(credit ? { credit } : {}) },
      {
        onSuccess: (d) => {
          setSheet(null);
          go(d.ref);
          if (askFuture(d, ["lines"])) return;
          toast("Prices changed for this visit", {
            undo: () => lines.mutate({ ref: d.ref, lines: before, scope: "this" }, { onSuccess: (x) => go(x.ref) }),
          });
        },
        onError: (e) => {
          setNeedCredit(e instanceof ApiError && e.code === "credit_choice_needed");
          setLineError(errText(e));
        },
      },
    );
  };
  const team = (people.data ?? []).filter((p) => p.active).map((p) => ({ id: p.id, name: p.name, hint: p.role }));

  const sheets = v ? (
    <>
      {sheet === "schedule" ? (
        <ScheduleSheet
          open
          onClose={close}
          visit={v}
          onMoved={go}
          onChanged={(ref, date, changed) => setFuture({ ref, date, ticked: changed })}
        />
      ) : null}
      {future ? (
        <FutureSheet
          date={future.date}
          ticked={future.ticked}
          saving={applyFuture.isPending}
          onClose={() => setFuture(null)}
          onSave={(b) =>
            applyFuture.mutate(
              { ref: future.ref, ...b },
              {
                onSuccess: (d) => {
                  setFuture(null);
                  go(d.ref);
                  toast("Visits after this one changed too");
                },
                onError: fail,
              },
            )
          }
        />
      ) : null}
      {sheet === "team" ? (
        <TeamSheet
          open
          onClose={close}
          people={team}
          selected={v.crew.map((c) => c.id)}
          repeating={false}
          saving={crew.isPending}
          onSave={(ids, s) => {
            const before = v.crew.map((c) => c.id);
            crew.mutate(
              { ref: v.ref, crew: ids, scope: s },
              {
                onSuccess: (d) => {
                  setSheet(null);
                  go(d.ref);
                  if (askFuture(d, ["crew"])) return;
                  toast("Team changed for this visit", {
                    undo: () => crew.mutate({ ref: d.ref, crew: before, scope: "this" }, { onSuccess: (x) => go(x.ref) }),
                  });
                },
                onError: fail,
              },
            );
          }}
        />
      ) : null}
      <Modal
        title="Line items"
        open={sheet === "lines"}
        onClose={close}
        footer={
          <>
            <Button type="tertiary" onClick={close}>
              Cancel
            </Button>
            {needCredit ? (
              <>
                <Button type="secondary" loading={lines.isPending} onClick={() => saveLines("no_credit")}>
                  Don't credit
                </Button>
                <Button loading={lines.isPending} onClick={() => saveLines("credit")}>
                  Credit their account
                </Button>
              </>
            ) : (
              <Button loading={lines.isPending} onClick={() => saveLines()}>
                Save
              </Button>
            )}
          </>
        }
      >
        <div className="stack">
          {lineError ? <Banner type="error">{lineError}</Banner> : null}
          <LinesEditor lines={draft} onChange={setDraft} />
          {v.repeating ? (
            <Banner type="warning">
              This job repeats. Saving changes only this visit. Then you pick what the visits after it take.
            </Banner>
          ) : null}
        </div>
      </Modal>
      <Modal
        title="Did the client pay you?"
        open={sheet === "paid"}
        onClose={close}
        footer={
          <>
            <Button type="tertiary" onClick={close}>
              Cancel
            </Button>
            <Button loading={complete.isPending} onClick={() => doComplete(paid)}>
              Mark done
            </Button>
          </>
        }
      >
        <Choice
          label="Paid the crew"
          value={paid}
          onChange={setPaid}
          options={[
            { value: "no", label: "No" },
            { value: "cash", label: "Yes, cash (comes off their pay)" },
            { value: "company", label: "Company check" },
          ]}
        />
      </Modal>
    </>
  ) : null;

  return {
    open,
    markDone,
    completing: complete.isPending,
    /** Apply to future visits, from the More menu. */
    openFuture: () => v && setFuture({ ref: v.ref, date: v.date, ticked: [] }),
    sheets,
  };
}
